import clsx from "clsx";
import { useRouter } from "next/router";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useViewport } from "@widgets/device-info";

import type { FilterItemType } from "@shared/content-types/types";
import { useCheckSize } from "@shared/ui/filters/use-check-size";

import { FiltersDropdown } from "../filters-dropdown";
import { FiltersList } from "../filters-list";

import s from "./filters.module.scss";

export type FiltersProps = ComponentProps<"div"> & {
  className?: string;
  filters: FilterItemType[];
  onChangeFilter: (filter: string) => void;
  hasAllItem?: boolean;
  dropdownFieldLabel?: string;
  allLabel?: string;
};

export const Filters = (props: FiltersProps) => {
  const {
    className,
    filters,
    onChangeFilter,
    hasAllItem = true,
    allLabel = "Все",
    dropdownFieldLabel,
    children,
  } = props;

  const ALL_ITEM: FilterItemType = useMemo(() => {
    return {
      id: 0,
      documentId: "all",
      type: allLabel,
      uid: "all",
      createdAt: "1970-01-01T00:00:00.000Z",
      updatedAt: "1970-01-01T00:00:00.000Z",
      publishedAt: "1970-01-01T00:00:00.000Z",
    };
  }, [allLabel]);

  const { isMobile } = useViewport();

  const route = useRouter();

  const [isLargest, setIsLargest] = useState(false);

  const filtersWithAll = useMemo(() => {
    if (hasAllItem) {
      return [ALL_ITEM, ...filters];
    }
    return filters;
  }, [filters, hasAllItem]);

  const [activeFilter, setActiveFilter] = useState<string>(
    typeof route?.query?.type === "string"
      ? route.query.type
      : filtersWithAll[0]?.uid || "all",
  );

  // Ref to track if it's the initial render
  const isInitialRender = useRef(true);

  useEffect(() => {
    const queryType =
      typeof route?.query?.type === "string" ? route.query.type : undefined;

    if (queryType) {
      setActiveFilter((prev) => (prev === queryType ? prev : queryType));
      isInitialRender.current = false;
      return;
    }

    // Only set the active filter on initial render
    if (isInitialRender.current) {
      setActiveFilter(filtersWithAll[0]?.uid || "all");
      isInitialRender.current = false;
    }
  }, [filtersWithAll, route?.query?.type]);

  const toggleSetFilter = useCallback(
    (value: string) => {
      setActiveFilter((prev) => {
        if (prev === value && hasAllItem) {
          return filtersWithAll[0]?.uid || "all";
        }
        return value;
      });
    },
    [filtersWithAll, hasAllItem],
  );

  const isMore = useMemo(() => {
    const viewFiltersCount = isMobile ? 1 : 5;
    return filtersWithAll.length > viewFiltersCount;
  }, [filtersWithAll, isMobile]);

  useEffect(() => {
    onChangeFilter(activeFilter);
  }, [onChangeFilter, activeFilter]);

  const isDropdownView = isMore || isLargest;

  const { $root, $child } = useCheckSize(setIsLargest);

  return (
    <div className={clsx(s.root, className)} ref={$root}>
      {isDropdownView && (
        <FiltersDropdown
          filters={filtersWithAll}
          onFilterClick={toggleSetFilter}
          activeFilter={activeFilter}
          fieldLabel={dropdownFieldLabel}
        />
      )}
      <FiltersList
        filters={filtersWithAll}
        onFilterClick={toggleSetFilter}
        activeFilter={activeFilter}
        ref={$child}
        className={clsx({
          [s.hide]: isDropdownView,
        })}
      />
      {children && <div className={s.rightSlot}>{children}</div>}
    </div>
  );
};

Filters.displayName = "Filters";
